/**
 * Organization (business account) Detail Page
 * Pinned summary header, then sections in the business-page order (requirements p.2/p.4):
 * Basic Information -> Address -> Business / Billing (+ external links) on Overview, then Contacts (POCs),
 * Metadata (incl. Status / Inactivity), Paperwork, Interactions (activity), Documents (files), followed by the
 * operational tabs (Locations, Calls to Action, Promo Codes, Linked Orgs, Status History, Reviews, Offers).
 */

import React, { useState, useCallback, lazy, Suspense } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PencilIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  UserGroupIcon,
  QrCodeIcon,
  ChatBubbleLeftRightIcon,
  TagIcon,
  ClockIcon,
  DocumentIcon,
  PlusIcon,
  LinkIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentCheckIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/24/outline";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/ui/Card";
import OrganizationPromoCodesTab from "../components/organizations/OrganizationPromoCodesTab";
import { isFieldManagerRole } from "../utils/fieldManagerAccess";
import InteractionsTab from "../components/interactions/InteractionsTab";
import Badge from "../components/ui/Badge";
import StatusToggle from "../components/common/StatusToggle";
import { useToast } from "../components/ui/Toast";
import {
  formatPhoneNumber,
  formatDate,
  formatDateMDY,
} from "../utils/formatters";
import {
  REQUIRED_FIELD_LABELS,
  describeOrganizationSaveError,
  getMissingRequiredOrgFields,
} from "../utils/organizationRequiredFields";
import OrganizationSummaryHeader from "../components/organizations/OrganizationSummaryHeader";
import ExternalLinksCard from "../components/organizations/ExternalLinksCard";
// Business Account Pages slice 2: people, vendor, files, program (self-contained components)
import OrganizationPocsTab from "../components/organizations/OrganizationPocsTab";
import OrganizationAssignmentsCard from "../components/organizations/OrganizationAssignmentsCard";
import VendorProfileCard from "../components/organizations/VendorProfileCard";
import ProgramAffiliationPanel from "../components/organizations/ProgramAffiliationPanel";
import SponsoredMembershipsPanel from "../components/organizations/SponsoredMembershipsPanel";
import BusinessCompAllotmentPanel from "../components/organizations/BusinessCompAllotmentPanel";
import OrganizationDocumentsTab from "../components/organizations/OrganizationDocumentsTab";
import OrganizationHeaderBadges from "../components/organizations/OrganizationHeaderBadges";
import {
  countOrganizationDocuments,
  getOrganizationDocuments,
} from "../services/documents.service";
import {
  getOrganization,
  getOrganizationStats,
  updateOrganization,
  getLinkedOrganizations,
  setOrganizationArchived,
} from "../services/organizations.service";
import { getLocations, deleteLocation } from "../services/locations.service";
import { getContacts } from "../services/contacts.service";
import type {
  Location,
  OrganizationStatusHistory,
} from "../types/organization.types";
import {
  ORGANIZATION_TYPE_OPTIONS,
  organizationTypeLabel,
} from "../types/organization.types";
import { getOrganizationStatusHistory } from "../services/organizations.service";
import OrganizationCTATab from "../components/organizations/OrganizationCTATab";
import LinkedOrganizationsTab from "../components/organizations/LinkedOrganizationsTab";
import { useAuth } from "../contexts/AuthContext";
import {
  canManageOrganizationCTAs,
  isReadonlyRole,
  normalizeRole,
} from "../utils/roleUtils";
import { useConfirmDialog } from "../components/ui/ConfirmDialog";

// Lazy-load map modal to avoid loading Leaflet until needed
const LocationMapModal = lazy(
  () => import("../components/organizations/LocationMapModal"),
);
import AddOrganizationContactModal from "../components/organizations/AddOrganizationContactModal";
import MetadataForm from "../components/organizations/MetadataForm";
import { getAccountReviews } from "../services/salesTeam.service";
import { getPartnerOffers } from "../services/partnerOffers.service";
import { REVIEW_STATUS_OPTIONS } from "../types/sales.types";
import { supabase } from "../services/supabase";
import {
  createReportSchedule,
  updateReportSchedule,
  deleteReportSchedule,
  SCHEDULE_NO_LONGER_EDITABLE,
} from "../services/reports.service";
import type { ReportSchedule } from "../types/event.types";
import {
  EnvelopeIcon,
  XMarkIcon,
  ChartBarIcon,
  TrashIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";

type TabId =
  | "overview"
  | "locations"
  | "contacts"
  | "calls-to-action"
  | "linked-organizations"
  | "metadata"
  | "paperwork"
  | "interactions"
  | "promo-codes"
  | "status-history"
  | "documents"
  | "reviews"
  | "partner-offers"
  | "promoter-report";

interface Tab {
  id: TabId;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  count?: number;
}

const OrganizationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { confirm: confirmArchive, ConfirmDialogComponent } =
    useConfirmDialog();
  const canManageArchive = ["admin", "superadmin"].includes(
    normalizeRole(user?.role),
  );
  // Mirrors can_manage_organization_ctas() (migration 2026-10-02-080). Other roles that can open this
  // page (support_agent) see Calls to Action and location promo codes read-only.
  const canManageCTAs = canManageOrganizationCTAs(user?.role);
  // organization_external_links RLS = is_internal_staff() (migration 2026-10-05-303); every role that can open this
  // page is internal staff, so the page offers link editing to all of them.
  const canEditLinks = [
    "superadmin",
    "admin",
    "manager",
    "regionalmanager",
    "territorymanager",
    "staff",
    "supportadmin",
    "supportagent",
  ].includes(normalizeRole(user?.role));
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  // Read-only viewer (owner 2026-11-03): overview, contacts, metadata, paperwork, documents and locations, view only.
  // No add / edit / delete / status / archive controls; the database refuses its writes regardless (migration 1622).
  const readOnly = isReadonlyRole(user?.role);
  // slice 2: "Business + Vendor" create flow lands here with ?addVendor=1 to open the vendor editor
  const [searchParams] = useSearchParams();
  const startVendorEditor = searchParams.get("addVendor") === "1";
  const { data: documentCount = 0 } = useQuery({
    queryKey: ["organization-documents-count", id],
    queryFn: () => countOrganizationDocuments(id!),
    enabled: !!id,
  });
  const { data: orgDocuments = [] } = useQuery({
    queryKey: ["organization-documents", id],
    queryFn: () => getOrganizationDocuments(id!),
    enabled: !!id && activeTab === "overview",
  });
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [mapLocation, setMapLocation] = useState<Location | null>(null);
  const [editMetadata, setEditMetadata] = useState<Record<
    string,
    unknown
  > | null>(null);

  // Fetch organization data
  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organization", id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });

  // Fetch organization stats
  const { data: stats } = useQuery({
    queryKey: ["organization-stats", id],
    queryFn: () => getOrganizationStats(id!),
    enabled: !!id,
  });

  // Fetch locations
  const { data: locationsData } = useQuery({
    queryKey: ["locations", id],
    queryFn: () => getLocations(id!, { page: 1, limit: 100 }),
    enabled: !!id,
  });

  const locations = locationsData?.data || [];

  // Each location's own promo code lives on its location-scoped CTA (parameters.promo) — the same
  // source the event Location dropdown reads. A location without one falls back to the
  // organization's code.
  const { data: locationPromos } = useQuery({
    queryKey: ["organization-location-promos", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_ctas")
        .select("location_id, parameters")
        .eq("organization_id", id!)
        .eq("is_active", true)
        .not("location_id", "is", null)
        .limit(1000);
      if (error) {
        throw error;
      }
      const byLocation = new Map<string, string>();
      for (const c of (data ?? []) as Array<{
        location_id: string | null;
        parameters: Record<string, unknown> | null;
      }>) {
        const promo =
          typeof c.parameters?.promo === "string"
            ? c.parameters.promo.trim()
            : "";
        if (c.location_id && promo) {
          byLocation.set(c.location_id, promo);
        }
      }
      return byLocation;
    },
    enabled: !!id && activeTab === "locations",
  });

  // "Set a location promo code" hands the location to the Calls to Action tab, which opens its
  // Add CTA form scoped to that location.
  const [ctaPresetLocationId, setCtaPresetLocationId] = useState<string | null>(
    null,
  );
  const clearCtaPreset = useCallback(() => setCtaPresetLocationId(null), []);

  // Fetch contacts
  const { data: contactsData } = useQuery({
    queryKey: ["contacts", id],
    queryFn: () =>
      getContacts({ page: 1, limit: 100 }, { organization_id: id }),
    enabled: !!id,
  });

  const contacts = contactsData?.data || [];

  // Fetch status history
  const { data: statusHistory } = useQuery({
    queryKey: ["organization-status-history", id],
    queryFn: () => getOrganizationStatusHistory(id!),
    enabled: !!id,
  });

  // Fetch linked organizations count for tab badge
  const { data: linkedOrgs } = useQuery({
    queryKey: ["organization-links", id],
    queryFn: () => getLinkedOrganizations(id!),
    enabled: !!id,
  });

  // Fetch pending promoter report count for tab badge
  const { data: promoterReportCount = 0 } = useQuery({
    queryKey: ["promoter-report-count", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("report_schedules")
        .select("id")
        .eq("organization_id", id!)
        .eq("report_type", "promoter_summary")
        .eq("is_active", true)
        .is("last_sent_at", null);
      if (error) {
        return 0;
      }
      return data?.length || 0;
    },
    enabled:
      !!id && organization?.organization_type === "promoter" && !readOnly,
    staleTime: 30000,
  });

  // Fetch account reviews for this organization
  const { data: orgReviews } = useQuery({
    queryKey: ["account-reviews", { organization_id: id }],
    queryFn: () => getAccountReviews({ organization_id: id }),
    enabled: !!id && !readOnly,
  });

  // FR-02: partner offers whose Source Organization is this org.
  const { data: orgPartnerOffers = [] } = useQuery({
    queryKey: ["org-partner-offers", id],
    queryFn: () => getPartnerOffers({ source_organization_id: id }),
    enabled: !!id && !readOnly,
    staleTime: 30000,
  });

  // Toggle organization status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async (newStatus: "active" | "inactive") => {
      console.log(
        "[OrganizationDetail] Toggle status mutation starting, newStatus:",
        newStatus,
      );
      if (!id) {
        throw new Error("No organization ID");
      }

      const result = await updateOrganization(id, { status: newStatus });
      console.log("[OrganizationDetail] Toggle status successful");
      return result;
    },
    onSuccess: async (_, newStatus) => {
      console.log("[OrganizationDetail] Toggle status onSuccess called");
      const action = newStatus === "active" ? "activated" : "deactivated";
      showToast("success", `Organization ${action} successfully`);

      // Force refetch the organization data
      await queryClient.refetchQueries({ queryKey: ["organization", id] });
      console.log(
        "[OrganizationDetail] Organization data refetched after toggle",
      );
    },
    onError: (error) => {
      console.error("[OrganizationDetail] Toggle status error:", error);
      const message =
        error instanceof Error
          ? error.message
          : String((error as { message?: string })?.message ?? "");
      showToast(
        "error",
        message
          ? describeOrganizationSaveError(message)
          : "Failed to update organization status",
      );
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (isArchived: boolean) => {
      if (!id) {
        throw new Error("No organization ID");
      }
      return setOrganizationArchived(id, isArchived);
    },
    onSuccess: async (_, isArchived) => {
      showToast(
        "success",
        isArchived
          ? "Organization disabled and hidden from lists"
          : "Organization restored successfully",
      );
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });
      await queryClient.refetchQueries({ queryKey: ["organization", id] });
    },
    onError: (archiveError: Error) => {
      showToast(
        "error",
        archiveError.message || "Failed to update organization visibility",
      );
    },
  });

  const handleArchiveChange = async (isArchived: boolean) => {
    const accepted = await confirmArchive({
      title: isArchived ? "Disable organization?" : "Restore organization?",
      message: isArchived
        ? "This organization will be hidden from organization lists, searches, and new operational assignments. Existing vouchers, events, and reports will keep their historical association."
        : "This organization will become available in lists and searches again. It will return to its previous status when possible.",
      confirmText: isArchived ? "Disable organization" : "Restore organization",
      variant: isArchived ? "danger" : "info",
    });

    if (accepted) {
      archiveMutation.mutate(isArchived);
    }
  };

  // Save metadata mutation
  const saveMetadataMutation = useMutation({
    mutationFn: async (newMetadata: Record<string, unknown>) => {
      if (!id) {
        throw new Error("No organization ID");
      }
      return updateOrganization(id, { metadata: newMetadata } as Partial<
        typeof organization
      >);
    },
    onSuccess: () => {
      showToast("success", "Metadata saved successfully");
      setEditMetadata(null);
      queryClient.refetchQueries({ queryKey: ["organization", id] });
    },
    onError: () => {
      showToast("error", "Failed to save metadata");
    },
  });

  // Handle toggle status change
  const handleToggleStatus = (isActive: boolean) => {
    console.log(
      "[OrganizationDetail] handleToggleStatus called with isActive:",
      isActive,
    );
    if (!id) {
      console.log("[OrganizationDetail] No organization ID, returning");
      return;
    }

    const newStatus = isActive ? "active" : "inactive";
    console.log(
      "[OrganizationDetail] Calling toggle mutation with newStatus:",
      newStatus,
    );
    toggleStatusMutation.mutate(newStatus);
  };

  // Define tabs with counts. Order follows the business-page section order; operational tabs follow.
  const allTabs: Tab[] = [
    { id: "overview", label: "Overview", icon: BuildingOfficeIcon },
    {
      id: "contacts",
      label: "Contacts",
      icon: UserGroupIcon,
      count: stats?.total_contacts || 0,
    },
    {
      id: "metadata",
      label: "Metadata",
      icon: AdjustmentsHorizontalIcon,
    },
    {
      id: "paperwork",
      label: "Paperwork",
      icon: ClipboardDocumentCheckIcon,
    },
    {
      id: "interactions",
      label: "Interactions",
      icon: ChatBubbleLeftRightIcon,
    },
    {
      id: "documents",
      label: "Documents",
      icon: DocumentIcon,
      count: documentCount,
    },
    {
      id: "locations",
      label: "Locations",
      icon: MapPinIcon,
      count: stats?.total_locations || 0,
    },
    { id: "calls-to-action", label: "Calls to Action", icon: QrCodeIcon },
    { id: "promo-codes", label: "Promo Codes", icon: TagIcon },
    {
      id: "linked-organizations",
      label: "Linked Orgs",
      icon: LinkIcon,
      count: linkedOrgs?.length || 0,
    },
    { id: "status-history", label: "Status History", icon: ClockIcon },
    { id: "reviews", label: "Reviews", icon: ClipboardDocumentCheckIcon },
    {
      id: "partner-offers",
      label: "Partner Offers",
      icon: ReceiptPercentIcon,
      count: orgPartnerOffers.length || 0,
    },
    ...(organization?.organization_type === "promoter"
      ? [
          {
            id: "promoter-report" as TabId,
            label: "Promoter Report",
            icon: ChartBarIcon,
            ...(promoterReportCount > 0 ? { count: promoterReportCount } : {}),
          },
        ]
      : []),
  ];
  // Regional / Territory Managers (owner 2026-10-29): Interactions, Account Reviews and Partner Offers are not
  // their modules (the database refuses them Interactions too).
  const READONLY_TABS: TabId[] = [
    "overview",
    "contacts",
    "metadata",
    "paperwork",
    "documents",
    "locations",
  ];
  const tabs = readOnly
    ? allTabs.filter((t) => READONLY_TABS.includes(t.id))
    : isFieldManagerRole(user?.role)
      ? allTabs.filter(
          (t) => !["interactions", "reviews", "partner-offers"].includes(t.id),
        )
      : allTabs;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading organization..." />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="p-6">
        <Card
          variant="outlined"
          padding="md"
          className="border-red-200 bg-red-50"
        >
          <h3 className="text-red-800 font-semibold">
            Error loading organization
          </h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : "Organization not found"}
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/organizations")}
            className="mt-4"
          >
            Back to Organizations
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pinned summary header */}
      <OrganizationSummaryHeader
        organization={organization}
        onBack={() => navigate("/organizations")}
        badges={
          <OrganizationHeaderBadges
            organizationId={organization.id}
            organizationType={organization.organization_type}
          />
        }
        pinnedBadges={
          <OrganizationHeaderBadges
            organizationId={organization.id}
            organizationType={organization.organization_type}
            compact
          />
        }
        actions={
          readOnly ? (
            <span
              className="text-sm text-gray-500"
              data-testid="organization-view-only"
            >
              View only
            </span>
          ) : (
            <>
              {canManageArchive && !organization.is_no_reseller && (
                <Button
                  variant={organization.is_archived ? "secondary" : "danger"}
                  onClick={() => handleArchiveChange(!organization.is_archived)}
                  isLoading={archiveMutation.isPending}
                  className="flex items-center gap-2"
                >
                  {organization.is_archived ? (
                    <ArrowPathIcon className="h-5 w-5" />
                  ) : (
                    <ArchiveBoxIcon className="h-5 w-5" />
                  )}
                  <span>
                    {organization.is_archived
                      ? "Restore Organization"
                      : "Disable Organization"}
                  </span>
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => navigate(`/organizations/${id}/locations/new`)}
                disabled={organization.is_archived}
              >
                + Add Location
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate(`/organizations/${id}/edit`)}
                className="flex items-center space-x-2"
                disabled={organization.is_archived}
              >
                <PencilIcon className="h-5 w-5" />
                <span>Edit Organization</span>
              </Button>
            </>
          )
        }
      />

      {organization.is_archived && (
        <div
          role="status"
          className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          This organization is disabled. It is hidden from lists, searches,
          maps, and new assignments, while historical records remain available.
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-1 overflow-x-auto">
          {tabs.map(({ id: tabId, label, icon: Icon, count }) => (
            <button
              key={tabId}
              onClick={() => setActiveTab(tabId)}
              className={`
                flex items-center space-x-2 py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                ${
                  activeTab === tabId
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              <Icon className="h-5 w-5" />
              <span>{label}</span>
              {count !== undefined && (
                <span
                  className={`
                    ml-1 px-2 py-0.5 text-xs rounded-full
                    ${
                      activeTab === tabId
                        ? "bg-primary-100 text-primary-700"
                        : "bg-gray-100 text-gray-600"
                    }
                  `}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {/* OVERVIEW TAB: Basic Information -> Address -> Business / Billing -> External Links */}
        {activeTab === "overview" &&
          (() => {
            const missing = getMissingRequiredOrgFields(organization);
            const field = (
              label: string,
              value: React.ReactNode,
              testId?: string,
            ) => (
              <div data-testid={testId}>
                <dt className="text-sm text-gray-600 mb-1">{label}</dt>
                <dd className="text-base text-gray-900 break-words">
                  {value === null ||
                  value === undefined ||
                  (typeof value === "string" && value.trim() === "") ? (
                    <span className="text-gray-400">Not set</span>
                  ) : (
                    value
                  )}
                </dd>
              </div>
            );
            const link = (href?: string | null, label?: string) =>
              href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 break-all"
                >
                  {label || href}
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              ) : null;
            const addressLines = (
              lines: (string | null | undefined)[],
            ): React.ReactNode => {
              const shown = lines.filter((l) => l && String(l).trim());
              return shown.length ? (
                <span className="whitespace-pre-line">{shown.join("\n")}</span>
              ) : null;
            };
            const cityLine = (
              city?: string | null,
              state?: string | null,
              postal?: string | null,
            ) =>
              [[city, state].filter(Boolean).join(", "), postal]
                .filter((x) => x && String(x).trim())
                .join(" ");
            return (
              <div className="space-y-6">
                {missing.length > 0 && !organization.is_archived && (
                  <div
                    role="status"
                    data-testid="missing-required-notice"
                    className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  >
                    Missing required details:{" "}
                    <strong>
                      {missing.map((f) => REQUIRED_FIELD_LABELS[f]).join(", ")}
                    </strong>
                    . Other changes still save; complete these before activating
                    the organization.
                  </div>
                )}

                {/* 1. Basic Information */}
                <Card>
                  <div
                    className="flex items-center justify-between mb-6"
                    data-testid="section-basic"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      Basic Information
                    </h3>
                    {readOnly ? (
                      <Badge
                        variant={
                          organization.status === "active" ? "success" : "gray"
                        }
                      >
                        {organization.status === "active"
                          ? "Active"
                          : "Inactive"}
                      </Badge>
                    ) : (
                      <StatusToggle
                        checked={organization.status === "active"}
                        onChange={handleToggleStatus}
                        disabled={
                          toggleStatusMutation.isPending ||
                          organization.is_archived
                        }
                        activeLabel="Active"
                        inactiveLabel="Inactive"
                        size="md"
                      />
                    )}
                  </div>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {field("Business Name", organization.name, "basic-name")}
                    {field(
                      "Legal Name",
                      organization.legal_name,
                      "basic-legal-name",
                    )}
                    {field("Email", organization.email, "basic-email")}
                    {field(
                      "Phone",
                      organization.phone
                        ? formatPhoneNumber(organization.phone)
                        : null,
                      "basic-phone",
                    )}
                    {field(
                      "Website",
                      link(organization.website),
                      "basic-website",
                    )}
                    {field(
                      "Facility Type",
                      organizationTypeLabel(organization.organization_type),
                      "basic-type",
                    )}
                    {field(
                      "Promo Code",
                      organization.promo_code ? (
                        <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                          {organization.promo_code}
                        </span>
                      ) : null,
                      "basic-promo",
                    )}
                    {field(
                      "Facility ID",
                      organization.facility_id ? (
                        <span className="font-semibold text-blue-700">
                          {organization.facility_id}
                        </span>
                      ) : null,
                      "basic-facility-id",
                    )}
                    {field(
                      "Organization ID",
                      organization.organization_id ? (
                        <span>
                          <span className="font-mono font-semibold">
                            {organization.organization_id}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            Assigned automatically; cannot be changed
                          </span>
                        </span>
                      ) : null,
                      "basic-org-id",
                    )}
                    {field(
                      "Prospect",
                      organization.is_prospect ? "Yes" : "No",
                      "basic-prospect",
                    )}
                    <div className="md:col-span-2">
                      {field(
                        "Description",
                        organization.description ? (
                          <span className="whitespace-pre-line">
                            {organization.description}
                          </span>
                        ) : null,
                        "basic-description",
                      )}
                    </div>
                    {field(
                      "Created",
                      formatDateMDY(organization.created_at),
                      "basic-created",
                    )}
                    {field(
                      "Last Updated",
                      formatDateMDY(
                        organization.updated_at || organization.created_at,
                      ),
                      "basic-updated",
                    )}
                  </dl>
                </Card>

                {/* 2. Address */}
                <Card>
                  <h3
                    className="text-lg font-semibold text-gray-900 mb-6"
                    data-testid="section-address"
                  >
                    Address
                  </h3>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {field(
                      "Physical Address",
                      addressLines([
                        organization.address_line1,
                        organization.address_line2,
                        cityLine(
                          organization.address_city,
                          organization.address_state,
                          organization.address_postal_code,
                        ),
                        organization.address_country,
                      ]),
                      "address-physical",
                    )}
                    {field(
                      "Mailing Address",
                      organization.mailing_same_as_physical ? (
                        <span className="text-gray-700">
                          Same as physical address
                        </span>
                      ) : (
                        addressLines([
                          organization.mailing_address_line1,
                          organization.mailing_address_line2,
                          cityLine(
                            organization.mailing_city,
                            organization.mailing_state,
                            organization.mailing_postal_code,
                          ),
                          organization.mailing_country,
                        ])
                      ),
                      "address-mailing",
                    )}
                  </dl>
                  {(organization.address_line1 ||
                    organization.address_city ||
                    organization.address_state) && (
                    <div className="mt-6 rounded-lg overflow-hidden border border-gray-200">
                      <iframe
                        title="Organization Location Map"
                        width="100%"
                        height="260"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                          [
                            organization.address_line1,
                            organization.address_city,
                            organization.address_state,
                            organization.address_postal_code,
                          ]
                            .filter(Boolean)
                            .join(", "),
                        )}`}
                      />
                    </div>
                  )}
                </Card>

                {/* 3. Business / Billing */}
                <Card>
                  <h3
                    className="text-lg font-semibold text-gray-900 mb-6"
                    data-testid="section-business"
                  >
                    Business / Billing Information
                  </h3>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {field(
                      "Account Number",
                      organization.account_number ? (
                        <span className="font-mono">
                          {organization.account_number}
                        </span>
                      ) : null,
                      "business-account-number",
                    )}
                    {field(
                      "Territory Numeric Designation",
                      organization.territory_designation ? (
                        <span className="font-mono">
                          {organization.territory_designation}
                        </span>
                      ) : null,
                      "business-territory",
                    )}
                    {field(
                      "Affiliate ID",
                      organization.affiliate_id,
                      "business-affiliate-id",
                    )}
                    {field("Region", organization.region, "business-region")}
                    {field(
                      "Zuora Link",
                      link(organization.zuora_link, "Zuora Account"),
                      "business-zuora",
                    )}
                  </dl>
                </Card>

                {/* slice 2: ownership/servicing, program affiliation, vendor details */}
                <OrganizationAssignmentsCard
                  organizationId={organization.id}
                  canEdit={canEditLinks && !organization.is_archived}
                  canManageTeam={canManageArchive}
                />
                <ProgramAffiliationPanel
                  organizationId={organization.id}
                  canEdit={canEditLinks && !organization.is_archived}
                />
                <SponsoredMembershipsPanel organizationId={organization.id} />
                <BusinessCompAllotmentPanel
                  organizationId={organization.id}
                  canLink={canManageArchive && !organization.is_archived}
                  canSetAllotment={
                    ["admin", "superadmin", "manager"].includes(
                      normalizeRole(user?.role),
                    ) && !organization.is_archived
                  }
                />
                <VendorProfileCard
                  organizationId={organization.id}
                  organizationName={organization.name}
                  organizationEmail={organization.email}
                  organizationPhone={organization.phone}
                  vendorOnly={!organization.organization_type?.trim()}
                  canEdit={canEditLinks && !organization.is_archived}
                  startEditing={startVendorEditor}
                  vendorFileCount={
                    orgDocuments.filter((d) => d.category === "vendor").length
                  }
                  onOpenFiles={() => setActiveTab("documents")}
                />

                <ExternalLinksCard
                  organizationId={organization.id}
                  zuoraLink={organization.zuora_link}
                  canEdit={canEditLinks && !organization.is_archived}
                />

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <Card
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setActiveTab("locations")}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Locations
                        </p>
                        <p className="mt-2 text-3xl font-bold text-gray-900">
                          {stats?.total_locations || 0}
                        </p>
                      </div>
                      <MapPinIcon className="h-12 w-12 text-red-500 opacity-50" />
                    </div>
                  </Card>
                  <Card
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setActiveTab("contacts")}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Contacts
                        </p>
                        <p className="mt-2 text-3xl font-bold text-gray-900">
                          {stats?.total_contacts || 0}
                        </p>
                      </div>
                      <UserGroupIcon className="h-12 w-12 text-blue-500 opacity-50" />
                    </div>
                  </Card>
                  <Card
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setActiveTab("documents")}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Documents
                        </p>
                        <p className="mt-2 text-3xl font-bold text-gray-900">
                          {documentCount}
                        </p>
                      </div>
                      <DocumentIcon className="h-12 w-12 text-gray-500 opacity-50" />
                    </div>
                  </Card>
                </div>
              </div>
            );
          })()}

        {/* LOCATIONS TAB */}
        {activeTab === "locations" && (
          <>
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Locations
                </h2>
                {!readOnly && (
                  <Button
                    onClick={() =>
                      navigate(`/organizations/${id}/locations/new`)
                    }
                    disabled={organization.is_archived}
                  >
                    <PlusIcon className="h-5 w-5 mr-1" />
                    Add Location
                  </Button>
                )}
              </div>
              {locations.length > 0 ? (
                <div className="overflow-x-auto">
                  {!readOnly && (
                    <p className="mb-3 text-xs text-gray-500">
                      A location&rsquo;s promo code comes from its own Call to
                      Action; events at that location use it. The code has to be
                      on this organization&rsquo;s{" "}
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-900 underline"
                        onClick={() => setActiveTab("promo-codes")}
                      >
                        Promo Codes
                      </button>{" "}
                      list first.
                    </p>
                  )}
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Promo Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Affiliate ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Facility ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Phone
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {locations.map((location: Location) => (
                        <tr key={location.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {location.name}
                            </div>
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm"
                            data-testid={`location-promo-${location.id}`}
                          >
                            {locationPromos?.get(location.id) ? (
                              <span className="font-medium text-gray-900">
                                {locationPromos.get(location.id)}
                              </span>
                            ) : (
                              <div>
                                <span className="text-gray-500">
                                  {organization.promo_code
                                    ? `Uses organization default (${organization.promo_code})`
                                    : "None set"}
                                </span>
                                {!organization.is_archived && canManageCTAs && (
                                  <button
                                    type="button"
                                    className="block text-xs text-blue-600 hover:text-blue-900 underline"
                                    onClick={() => {
                                      setCtaPresetLocationId(location.id);
                                      setActiveTab("calls-to-action");
                                    }}
                                  >
                                    Set a location promo code
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {location.affiliate_id || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {location.facility_id || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {location.address_line1 || location.city
                              ? `${location.address_line1 || ""} ${location.city || ""} ${location.state || ""}`.trim()
                              : "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {location.phone
                              ? formatPhoneNumber(location.phone)
                              : "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                            {(location.latitude && location.longitude) ||
                            location.address_line1 ? (
                              <button
                                onClick={() => setMapLocation(location)}
                                className="text-green-600 hover:text-green-900 transition-colors"
                                title="View on map"
                              >
                                Map
                              </button>
                            ) : null}
                            {!readOnly && (
                              <>
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/organizations/${id}/locations/${location.id}/edit`,
                                    )
                                  }
                                  disabled={organization.is_archived}
                                  className={
                                    organization.is_archived
                                      ? "text-gray-400 cursor-not-allowed"
                                      : "text-blue-600 hover:text-blue-900 transition-colors"
                                  }
                                >
                                  Edit
                                </button>
                                <button
                                  disabled={organization.is_archived}
                                  className={
                                    organization.is_archived
                                      ? "text-gray-400 cursor-not-allowed"
                                      : "text-red-600 hover:text-red-900 transition-colors"
                                  }
                                  onClick={async () => {
                                    if (
                                      !confirm(
                                        `Delete location "${location.name}"? This will archive the location.`,
                                      )
                                    ) {
                                      return;
                                    }
                                    try {
                                      await deleteLocation(location.id);
                                      showToast(
                                        "success",
                                        "Location deleted successfully",
                                      );
                                      queryClient.refetchQueries({
                                        queryKey: ["organization", id],
                                      });
                                    } catch (err: any) {
                                      showToast(
                                        "error",
                                        err.message ||
                                          "Failed to delete location",
                                      );
                                    }
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No locations
                  </h3>
                  {!readOnly && (
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by adding a new location.
                    </p>
                  )}
                  {!readOnly && (
                    <div className="mt-6">
                      <Button
                        onClick={() =>
                          navigate(`/organizations/${id}/locations/new`)
                        }
                        disabled={organization.is_archived}
                      >
                        <PlusIcon className="h-5 w-5 mr-1" />
                        Add First Location
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Location Map Modal */}
            {mapLocation && (
              <Suspense fallback={null}>
                <LocationMapModal
                  location={mapLocation}
                  onClose={() => setMapLocation(null)}
                />
              </Suspense>
            )}
          </>
        )}

        {/* CONTACTS TAB */}
        {activeTab === "contacts" && id && (
          <OrganizationPocsTab
            organizationId={id}
            otherContacts={contacts}
            canEdit={canEditLinks && !organization.is_archived}
            onAddOtherContact={() => setIsAddContactOpen(true)}
          />
        )}

        {/* CALLS TO ACTION TAB */}
        {activeTab === "calls-to-action" && (
          <OrganizationCTATab
            organization={organization}
            canEdit={canManageCTAs}
            presetLocationId={ctaPresetLocationId}
            onPresetConsumed={clearCtaPreset}
          />
        )}

        {/* LINKED ORGANIZATIONS TAB */}
        {activeTab === "linked-organizations" && (
          <LinkedOrganizationsTab organizationId={id!} />
        )}

        {/* METADATA TAB */}
        {activeTab === "metadata" && (
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Organization Metadata
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  These values apply to all locations by default. Locations can
                  override individual fields.
                </p>
              </div>
              {!readOnly && (
                <div className="flex items-center space-x-3">
                  {editMetadata !== null && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditMetadata(null)}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={saveMetadataMutation.isPending}
                    onClick={() => {
                      if (editMetadata !== null) {
                        saveMetadataMutation.mutate(editMetadata);
                      } else {
                        setEditMetadata(
                          (organization.metadata as Record<string, unknown>) ||
                            {},
                        );
                      }
                    }}
                  >
                    {saveMetadataMutation.isPending
                      ? "Saving..."
                      : editMetadata !== null
                        ? "Save Metadata"
                        : "Edit Metadata"}
                  </Button>
                </div>
              )}
            </div>
            <MetadataForm
              metadata={
                editMetadata !== null
                  ? editMetadata
                  : (organization.metadata as Record<string, unknown>) || {}
              }
              onChange={(val) => setEditMetadata(val)}
              readOnly={editMetadata === null}
              categories={[
                "status_inactivity",
                "facility_use",
                "llp_tracking",
                "program_products",
              ]}
            />
          </Card>
        )}

        {/* PAPERWORK TAB (same metadata record; the paperwork checklist on its own) */}
        {activeTab === "paperwork" && (
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Paperwork / Onboarding Checklist
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Each item shows its status, the date it was completed and an
                  optional link to the signed document.
                </p>
              </div>
              {!readOnly && (
                <div className="flex items-center space-x-3">
                  {editMetadata !== null && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditMetadata(null)}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={saveMetadataMutation.isPending}
                    onClick={() => {
                      if (editMetadata !== null) {
                        saveMetadataMutation.mutate(editMetadata);
                      } else {
                        setEditMetadata(
                          (organization.metadata as Record<string, unknown>) ||
                            {},
                        );
                      }
                    }}
                  >
                    {saveMetadataMutation.isPending
                      ? "Saving..."
                      : editMetadata !== null
                        ? "Save Paperwork"
                        : "Edit Paperwork"}
                  </Button>
                </div>
              )}
            </div>
            <MetadataForm
              metadata={
                editMetadata !== null
                  ? editMetadata
                  : (organization.metadata as Record<string, unknown>) || {}
              }
              onChange={(val) => setEditMetadata(val)}
              readOnly={editMetadata === null}
              categories={["paperwork"]}
            />
          </Card>
        )}

        {/* INTERACTIONS TAB: servicing log (business pages slice 3) */}
        {activeTab === "interactions" && id && (
          <Card>
            <InteractionsTab organizationId={id} />
          </Card>
        )}

        {/* PROMO CODES TAB */}
        {activeTab === "promo-codes" && id && (
          <OrganizationPromoCodesTab organizationId={id} />
        )}

        {/* STATUS HISTORY TAB */}
        {activeTab === "status-history" && (
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Status History
            </h2>
            {statusHistory && statusHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Field
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Previous Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        New Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Changed By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {statusHistory.map((record: OrganizationStatusHistory) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateMDY(record.changed_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                          {record.field_changed === "is_prospect"
                            ? "Prospect"
                            : record.field_changed.replace("_", " ")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.field_changed === "is_prospect"
                            ? record.old_value === "true"
                              ? "Yes"
                              : "No"
                            : record.field_changed === "organization_type"
                              ? ORGANIZATION_TYPE_OPTIONS.find(
                                  (o) => o.value === record.old_value,
                                )?.label ||
                                record.old_value ||
                                "-"
                              : record.old_value || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.field_changed === "is_prospect"
                            ? record.new_value === "true"
                              ? "Yes"
                              : "No"
                            : record.field_changed === "organization_type"
                              ? ORGANIZATION_TYPE_OPTIONS.find(
                                  (o) => o.value === record.new_value,
                                )?.label ||
                                record.new_value ||
                                "-"
                              : record.new_value || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.changed_by_user?.full_name ||
                            record.changed_by_user?.email ||
                            "System"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No status changes
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Status change history will appear here when you update status,
                  prospect flag, or organization type.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* DOCUMENTS TAB */}
        {activeTab === "documents" && id && (
          <OrganizationDocumentsTab
            organizationId={id}
            canEdit={canEditLinks && !organization.is_archived}
          />
        )}

        {/* REVIEWS TAB */}
        {activeTab === "reviews" && (
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Account Reviews ({orgReviews?.length || 0})
              </h2>
              <Button
                onClick={() => navigate(`/account-reviews/create?orgId=${id}`)}
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                New Review
              </Button>
            </div>

            {(orgReviews || []).length === 0 ? (
              <div className="text-center py-12">
                <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No account reviews
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Field visit reviews for this organization will appear here
                </p>
              </div>
            ) : (
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Review Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Submitted By
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(orgReviews || []).map((review) => {
                      const statusOpt = REVIEW_STATUS_OPTIONS.find(
                        (o) => o.value === review.status,
                      );
                      return (
                        <tr
                          key={review.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() =>
                            navigate(`/account-reviews/${review.id}`)
                          }
                        >
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {review.review_date
                              ? new Date(
                                  review.review_date,
                                ).toLocaleDateString()
                              : "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {review.submitted_by_profile?.full_name || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <Badge color={statusOpt?.color || "gray"} size="sm">
                              {statusOpt?.label || review.status}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* PARTNER OFFERS TAB (FR-02) — offers whose Source Organization is this org */}
        {activeTab === "partner-offers" && (
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Partner Offers ({orgPartnerOffers.length})
              </h2>
              <Button onClick={() => navigate(`/partner-offers`)}>
                <PlusIcon className="h-5 w-5 mr-1" />
                Manage Offers
              </Button>
            </div>

            {orgPartnerOffers.length === 0 ? (
              <div className="text-center py-12">
                <ReceiptPercentIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No partner offers
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Offers with this organization set as the Source Organization
                  will appear here.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Offer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Window
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {orgPartnerOffers.map((offer) => {
                      const isLightning =
                        String(offer.offer_kind) === "lightning";
                      return (
                        <tr
                          key={offer.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() =>
                            navigate(`/partner-offers/${offer.id}`)
                          }
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {offer.headline}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant={isLightning ? "warning" : "gray"}
                              size="sm"
                            >
                              {isLightning ? "Lightning" : "Standing"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {offer.offer_amount}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {isLightning && offer.start_date && offer.end_date
                              ? `${formatDate(offer.start_date)} – ${formatDate(offer.end_date)}`
                              : "Always"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                            {offer.status}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* PROMOTER REPORT TAB */}
        {activeTab === "promoter-report" && (
          <PromoterReportSection
            organizationId={id!}
            organizationName={organization?.name || ""}
            organizationEmail={organization?.email}
          />
        )}
      </div>

      {/* Add Contact Modal */}
      <AddOrganizationContactModal
        isOpen={isAddContactOpen}
        onClose={() => setIsAddContactOpen(false)}
        organizationId={id!}
        locations={locations}
      />
      <ConfirmDialogComponent />
    </div>
  );
};

// ============================================================================
// PROMOTER REPORT SECTION (inline — simple form, no separate component needed)
// ============================================================================

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Common US timezones (shared with ReportsTab)
const PROMOTER_TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
];

// Time options in 30-minute increments
const PROMOTER_TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (const m of [0, 30]) {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? "AM" : "PM";
    const min = m === 0 ? "00" : "30";
    const val = `${String(h).padStart(2, "0")}:${min}`;
    const label = `${hour12}:${min} ${ampm}`;
    PROMOTER_TIME_OPTIONS.push(`${val}|${label}`);
  }
}

const PromoterReportSection: React.FC<{
  organizationId: string;
  organizationName: string;
  organizationEmail?: string;
}> = ({ organizationId, organizationEmail }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [emailError, setEmailError] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("16:30");
  const [timezone, setTimezone] = useState("America/Chicago");
  const [dateTimeError, setDateTimeError] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [attachmentFormat, setAttachmentFormat] = useState<"csv" | "xlsx">(
    "csv",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  // Audit-AI 2026-05-28 Part 4: edit-vs-create disambiguator mirroring
  // ReportsTab. Null = new-schedule form; populated = editing that
  // schedule. Gated on schedule.last_sent_at IS NULL (same posture as
  // ReportsTab) so a sent schedule's row shows no Edit button.
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(
    null,
  );
  // 2026-06-04: separate signal for duplicate mode (per audit R2 must-fix).
  // When non-null, the form is creating a NEW schedule (editingScheduleId
  // is also null) but the header copy reads "Duplicate schedule (from
  // <source-date>)". Cleared on resetForm, save success, and on any
  // mode switch (openEditForm + openForm for a fresh create).
  const [duplicatingFromScheduleDate, setDuplicatingFromScheduleDate] =
    useState<string | null>(null);

  // Fetch existing promoter report schedules (multiple now)
  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ["promoter-report-schedules", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("report_schedules")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("report_type", "promoter_summary")
        .order("created_at", { ascending: false });
      if (error) {
        throw error;
      }
      return (data || []) as ReportSchedule[];
    },
    enabled: !!organizationId,
  });

  // Check for linked events
  const { data: linkedEvents = [] } = useQuery({
    queryKey: ["promoter-linked-events", organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("events")
        .select("id, title, start_time, end_time")
        .eq("promoter_id", organizationId)
        .order("start_time", { ascending: false })
        .limit(10);
      return data || [];
    },
    enabled: !!organizationId,
  });

  const todayStr = new Date().toISOString().slice(0, 10);

  const resetForm = () => {
    setShowForm(false);
    setEditingScheduleId(null);
    setDuplicatingFromScheduleDate(null);
    setEmailInput("");
    setEmails([]);
    setEmailError("");
    setScheduledDate("");
    setScheduledTime("16:30");
    setDateTimeError("");
    setDateFrom("");
    setDateTo("");
    setAttachmentFormat("csv");
    setIsSaving(false);
  };

  const openForm = () => {
    setShowForm(true);
    setEditingScheduleId(null);
    setDuplicatingFromScheduleDate(null);
    // Pre-populate org email if available
    if (organizationEmail) {
      const trimmed = organizationEmail.trim().toLowerCase();
      if (isValidEmail(trimmed)) {
        setEmails([trimmed]);
      }
    }
  };

  /**
   * Open the form in duplicate-mode pre-populated from an existing
   * schedule's values. Per audit R2 must-fix:
   *   - editingScheduleId stays null → save INSERTs new row
   *   - duplicatingFromScheduleDate is set for the header label
   *   - scheduledDate is cleared (operator must pick a new date)
   *   - scheduledTime preserved (promoters reuse end-of-day window)
   *   - timezone NOT inherited — uses form's current timezone state
   *   - date_from/date_to preserved (promoters duplicate within the
   *     same event window)
   */
  const openDuplicateForm = (schedule: ReportSchedule) => {
    setShowForm(true);
    setEditingScheduleId(null);
    setEmails(schedule.recipient_emails);
    setEmailInput("");
    setEmailError("");
    if (schedule.scheduled_at) {
      const stored = new Date(schedule.scheduled_at);
      const tzFormatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const parts = tzFormatter.formatToParts(stored);
      const pick = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
      const srcDate = `${pick("year")}-${pick("month")}-${pick("day")}`;
      const hm = `${pick("hour")}:${pick("minute")}`;
      setDuplicatingFromScheduleDate(srcDate);
      setScheduledDate("");
      setScheduledTime(hm);
    } else {
      setDuplicatingFromScheduleDate(null);
      setScheduledDate("");
      setScheduledTime("16:30");
    }
    setDateTimeError("");
    setDateFrom(schedule.date_from ?? "");
    setDateTo(schedule.date_to ?? "");
    // Carry forward the source's attachment_format so a duplicate of
    // an xlsx schedule defaults to xlsx.
    setAttachmentFormat(schedule.attachment_format ?? "csv");
  };

  /**
   * Open the form pre-populated with an existing schedule's values.
   * Mirror of ReportsTab's openEditForm (2026-05-25). Splits the
   * stored UTC scheduled_at into local date + time + timezone string
   * for the form, and routes date_from / date_to through directly
   * (already stored as YYYY-MM-DD strings).
   */
  const openEditForm = (schedule: ReportSchedule) => {
    setShowForm(true);
    setEditingScheduleId(schedule.id);
    setDuplicatingFromScheduleDate(null);
    setEmails(schedule.recipient_emails);
    setEmailInput("");
    setEmailError("");
    if (schedule.scheduled_at) {
      // Stored as ISO UTC. Convert to the configured timezone's local
      // wall-clock to pre-fill the date+time inputs. The existing
      // create-form uses America/Chicago as default, so use the same
      // for edit display so the operator sees a coherent local time.
      const stored = new Date(schedule.scheduled_at);
      const tzFormatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const parts = tzFormatter.formatToParts(stored);
      const pick = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
      const ymd = `${pick("year")}-${pick("month")}-${pick("day")}`;
      const hm = `${pick("hour")}:${pick("minute")}`;
      setScheduledDate(ymd);
      setScheduledTime(hm);
    } else {
      setScheduledDate("");
      setScheduledTime("16:30");
    }
    setDateTimeError("");
    setDateFrom(schedule.date_from ?? "");
    setDateTo(schedule.date_to ?? "");
    setAttachmentFormat(schedule.attachment_format ?? "csv");
  };

  const addEmail = () => {
    const trimmed = emailInput.trim().toLowerCase();
    setEmailError("");
    if (!trimmed) {
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailError(
        "Please enter an email address with a valid format, such as name@example.com.",
      );
      return;
    }
    if (emails.includes(trimmed)) {
      setEmailError(
        "This recipient has already been added. Please add a new email.",
      );
      return;
    }
    setEmails([...emails, trimmed]);
    setEmailInput("");
  };

  const removeEmail = (email: string) => {
    setEmails(emails.filter((e) => e !== email));
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleSubmit = async () => {
    setEmailError("");
    setDateTimeError("");

    if (emails.length === 0) {
      setEmailError("Add at least one recipient email.");
      return;
    }
    if (!scheduledDate) {
      setDateTimeError("Please select a date for the report.");
      return;
    }

    // Build UTC date from selected date/time/timezone
    const dateTimeStr = `${scheduledDate}T${scheduledTime}:00`;
    const localDate = new Date(dateTimeStr);
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(localDate);
    const offsetPart = parts.find((p) => p.type === "timeZoneName")?.value;

    let offsetMinutes = 0;
    if (offsetPart) {
      const match = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
      if (match) {
        const sign = match[1] === "+" ? 1 : -1;
        const hours = parseInt(match[2], 10);
        const mins = match[3] ? parseInt(match[3], 10) : 0;
        offsetMinutes = sign * (hours * 60 + mins);
      }
    }

    const utcMs =
      localDate.getTime() -
      localDate.getTimezoneOffset() * 60000 -
      offsetMinutes * 60000;
    const scheduledUtc = new Date(utcMs);

    if (scheduledUtc <= new Date()) {
      setDateTimeError(
        "Selected time occurs in the past. Please choose a different time.",
      );
      return;
    }

    setIsSaving(true);
    try {
      if (editingScheduleId) {
        // R2-SR2: explicit nulls for date_from / date_to clear the
        // stored value. Empty string from a date input → null.
        await updateReportSchedule(editingScheduleId, {
          recipient_emails: emails,
          scheduled_at: scheduledUtc.toISOString(),
          date_from: dateFrom ? dateFrom : null,
          date_to: dateTo ? dateTo : null,
          attachment_format: attachmentFormat,
        });
        showToast("success", "Promoter report schedule updated");
      } else {
        await createReportSchedule({
          report_type: "promoter_summary",
          organization_id: organizationId,
          recipient_emails: emails,
          scheduled_at: scheduledUtc.toISOString(),
          is_recurring: false,
          ...(dateFrom ? { date_from: dateFrom } : {}),
          ...(dateTo ? { date_to: dateTo } : {}),
          attachment_format: attachmentFormat,
        });
        showToast("success", "Promoter report scheduled");
      }
      queryClient.invalidateQueries({
        queryKey: ["promoter-report-schedules", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["promoter-report-count", organizationId],
      });
      resetForm();
    } catch (err: any) {
      if (err?.message === SCHEDULE_NO_LONGER_EDITABLE) {
        showToast(
          "error",
          "This schedule was sent or deactivated and can no longer be edited.",
        );
        queryClient.invalidateQueries({
          queryKey: ["promoter-report-schedules", organizationId],
        });
        resetForm();
      } else {
        showToast("error", err.message || "Failed to schedule report");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteReportSchedule(id);
      queryClient.invalidateQueries({
        queryKey: ["promoter-report-schedules", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["promoter-report-count", organizationId],
      });
      showToast("success", "Report schedule deleted");
      setConfirmDelete(null);
    } catch (err: any) {
      showToast("error", err.message || "Failed to delete schedule");
    }
  };

  const formatScheduleDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Promoter Report
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Schedule a one-time voucher summary report for all events linked
              to this promoter up to the scheduled date. Recipient emails in the
              report CSV are obscured for privacy.
            </p>
          </div>
          {!showForm && (
            <Button variant="primary" size="sm" onClick={openForm}>
              <PlusIcon className="w-4 h-4 mr-1" />
              Schedule Report
            </Button>
          )}
        </div>

        {/* Schedule Form — same JSX serves create and edit; the
            editingScheduleId state determines which mode and the
            handleSubmit dispatcher routes to createReportSchedule vs
            updateReportSchedule. Audit-AI 2026-05-28 Part 4. */}
        {showForm && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
            <h3 className="text-sm font-medium text-gray-900">
              {editingScheduleId
                ? "Edit Report Schedule"
                : duplicatingFromScheduleDate
                  ? `Duplicate schedule (from ${duplicatingFromScheduleDate})`
                  : "New Report Schedule"}
            </h3>

            {/* Email input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Emails
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailError("");
                  }}
                  onKeyDown={handleEmailKeyDown}
                  placeholder="Enter email and press Enter"
                  className={`flex-1 rounded-md shadow-sm text-sm ${
                    emailError
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                  }`}
                />
                <Button variant="secondary" size="sm" onClick={addEmail}>
                  Add
                </Button>
              </div>
              {emailError && (
                <p className="mt-1 text-sm text-red-600">{emailError}</p>
              )}

              {emails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {emails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                    >
                      <EnvelopeIcon className="w-3 h-3 mr-1" />
                      {email}
                      <button
                        onClick={() => removeEmail(email)}
                        className="ml-1.5 text-primary-600 hover:text-primary-900"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Report Date Range (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Date Range{" "}
                <span className="text-gray-400 font-normal text-xs">
                  (optional — defaults to all events up to scheduled date)
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Date / Time / Timezone */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => {
                    setScheduledDate(e.target.value);
                    setDateTimeError("");
                  }}
                  min={todayStr}
                  className={`w-full rounded-md shadow-sm text-sm ${
                    dateTimeError
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <select
                  value={scheduledTime}
                  onChange={(e) => {
                    setScheduledTime(e.target.value);
                    setDateTimeError("");
                  }}
                  className={`w-full rounded-md shadow-sm text-sm ${
                    dateTimeError
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                  }`}
                >
                  {PROMOTER_TIME_OPTIONS.map((opt) => {
                    const [val, label] = opt.split("|");
                    return (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                >
                  {PROMOTER_TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {dateTimeError && (
              <p className="text-sm text-red-600">{dateTimeError}</p>
            )}

            {/* Attachment format — added 2026-06-04 with the XLSX
                scheduled-reports ship. Defaults to CSV; opt in to
                xlsx per-schedule. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attachment Format
              </label>
              <div className="flex gap-3">
                <label className="inline-flex items-center text-sm text-gray-700">
                  <input
                    type="radio"
                    name="promoter_attachment_format"
                    value="csv"
                    checked={attachmentFormat === "csv"}
                    onChange={() => setAttachmentFormat("csv")}
                    className="mr-2 text-primary-600 focus:ring-primary-500"
                  />
                  CSV
                </label>
                <label className="inline-flex items-center text-sm text-gray-700">
                  <input
                    type="radio"
                    name="promoter_attachment_format"
                    value="xlsx"
                    checked={attachmentFormat === "xlsx"}
                    onChange={() => setAttachmentFormat("xlsx")}
                    className="mr-2 text-primary-600 focus:ring-primary-500"
                  />
                  Excel (.xlsx) — bold headers, sized columns, sorted
                </label>
              </div>
            </div>

            <p className="text-xs text-gray-400">
              {dateFrom || dateTo
                ? "The report will include voucher data for events within the specified date range."
                : "The report will include all voucher data for events linked to this promoter up to the scheduled date."}
            </p>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={isSaving || emails.length === 0 || !scheduledDate}
              >
                {isSaving
                  ? "Saving..."
                  : editingScheduleId
                    ? "Update Schedule"
                    : "Save Schedule"}
              </Button>
              <Button variant="secondary" size="sm" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Existing Schedules */}
        {schedules.length === 0 && !showForm ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <ClockIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              No report schedules yet. Click "Schedule Report" to set one up.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {schedules.map((schedule: ReportSchedule) => (
              <div
                key={schedule.id}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between"
              >
                <div className="space-y-1.5">
                  {/* Status badge */}
                  <div className="flex items-center gap-2">
                    {schedule.last_sent_at ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Sent
                      </span>
                    ) : schedule.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        Scheduled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </div>

                  {schedule.scheduled_at && (
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Scheduled:</span>{" "}
                      {formatScheduleDate(schedule.scheduled_at)}
                    </p>
                  )}

                  {schedule.last_sent_at && (
                    <p className="text-sm text-gray-500">
                      Sent: {formatScheduleDate(schedule.last_sent_at)}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {schedule.recipient_emails.map((email: string) => (
                      <span
                        key={email}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                      >
                        <EnvelopeIcon className="w-3 h-3 mr-1 text-gray-400" />
                        {email}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action buttons. Edit + Delete are gated on
                    !last_sent_at (audit-AI Part 4) — sent schedules
                    are immutable. Duplicate (2026-06-04) shows for
                    BOTH sent and unsent rows because it creates a
                    brand-new schedule with its own lifecycle. */}
                <div className="flex gap-1">
                  {confirmDelete === schedule.id ? (
                    <>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(schedule.id)}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setConfirmDelete(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      {!schedule.last_sent_at && (
                        <button
                          onClick={() => openEditForm(schedule)}
                          className="p-1.5 text-gray-400 hover:text-blue-500 rounded"
                          title="Edit schedule"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => openDuplicateForm(schedule)}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded inline-flex items-center gap-1"
                        title="Duplicate schedule (creates a new schedule with the same recipients and parameters)"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        Duplicate
                      </button>
                      {!schedule.last_sent_at && (
                        <button
                          onClick={() => setConfirmDelete(schedule.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                          title="Delete schedule"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Linked events info */}
        {linkedEvents.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              No events are currently linked to this promoter.
            </p>
          </div>
        ) : (
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Linked Events ({linkedEvents.length})
            </p>
            <div className="space-y-1">
              {linkedEvents.slice(0, 5).map((event: any) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between text-sm py-1"
                >
                  <span className="text-gray-700">{event.title}</span>
                  <span className="text-gray-400 text-xs">
                    {event.end_time
                      ? new Date(event.end_time).toLocaleDateString()
                      : "No end date"}
                  </span>
                </div>
              ))}
              {linkedEvents.length > 5 && (
                <p className="text-xs text-gray-400">
                  +{linkedEvents.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default OrganizationDetail;
